import { useState, useEffect } from 'react'
import { Link, useParams, useNavigate } from 'react-router-dom'
import { Helmet } from 'react-helmet-async'
import { motion } from 'framer-motion'
import { ArrowLeft, Clock, Calendar, Tag, ArrowRight, Share2, CheckCircle } from 'lucide-react'

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL
const API = `${BACKEND_URL}/api`

const categoryColors = {
  'IT Infrastructure': 'bg-blue-50 text-blue-700 border-blue-200',
  'Security & Infrastructure': 'bg-slate-50 text-slate-700 border-slate-200',
  'Business Technology': 'bg-indigo-50 text-indigo-700 border-indigo-200',
  'Case Study': 'bg-emerald-50 text-emerald-700 border-emerald-200',
  'Software Development': 'bg-violet-50 text-violet-700 border-violet-200',
}

function getCategoryStyle(cat) {
  return categoryColors[cat] || 'bg-[#EBF2FF] text-[#0047AB] border-[#0047AB]/20'
}

function formatDate(dateStr) {
  return new Date(dateStr).toLocaleDateString('en-AU', { year: 'numeric', month: 'long', day: 'numeric' })
}

export default function BlogPost() {
  const { slug } = useParams()
  const navigate = useNavigate()
  const [post, setPost] = useState(null)
  const [related, setRelated] = useState([])
  const [loading, setLoading] = useState(true)
  const [copied, setCopied] = useState(false)

  useEffect(() => {
    setLoading(true)
    Promise.all([
      fetch(`${API}/blog/${slug}`).then(r => { if (!r.ok) throw new Error('Not found'); return r.json() }),
      fetch(`${API}/blog`).then(r => r.json()),
    ]).then(([postData, allPosts]) => {
      setPost(postData)
      setRelated(allPosts.filter(p => p.slug !== slug && p.category === postData.category).slice(0, 2))
      setLoading(false)
      window.scrollTo(0, 0)
    }).catch(() => { setLoading(false); navigate('/blog') })
  }, [slug])

  const handleShare = async () => {
    const url = window.location.href
    if (navigator.share) {
      await navigator.share({ title: post.title, url })
    } else {
      await navigator.clipboard.writeText(url)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    }
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-[#FAFAFA] flex items-center justify-center">
        <div className="space-y-4 w-full max-w-2xl px-6">
          <div className="h-8 bg-[#F3F4F6] rounded-xl animate-pulse w-3/4" />
          <div className="h-4 bg-[#F3F4F6] rounded animate-pulse w-full" />
          <div className="h-4 bg-[#F3F4F6] rounded animate-pulse w-5/6" />
        </div>
      </div>
    )
  }

  if (!post) return null

  const canonicalUrl = `https://akshayrao.com.au/blog/${post.slug}`

  return (
    <>
      <Helmet>
        <title>{`${post.title} | Akshay Rao`}</title>
        <meta name="description" content={post.excerpt} />
        <meta property="og:type" content="article" />
        <meta property="og:title" content={post.title} />
        <meta property="og:image" content="https://akshayrao.com.au/og-image.jpg" />
        <meta property="article:published_time" content={post.created_at} />
        <meta property="article:author" content="Akshay Rao" />
        <meta property="article:tag" content={(post.tags || []).join(', ')} />
        <link rel="canonical" href={canonicalUrl} />
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Article",
          "headline": post.title,
          "description": post.excerpt,
          "author": { "@type": "Person", "name": "Akshay Rao", "url": "https://akshayrao.com.au" },
          "publisher": { "@type": "Person", "name": "Akshay Rao" },
          "datePublished": post.created_at,
          "dateModified": post.updated_at,
          "url": canonicalUrl,
          "image": "https://akshayrao.com.au/og-image.jpg",
          "keywords": (post.tags || []).join(', '),
        })}</script>
      </Helmet>

      <div className="min-h-screen bg-[#FAFAFA]">
        {/* Header bar */}
        <div className="bg-white border-b border-[#E5E7EB] pt-14 sticky top-0 z-40">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-12 h-12 flex items-center justify-between">
            <Link to="/blog" className="flex items-center gap-2 text-[#6B7280] text-sm font-body hover:text-[#0047AB] transition-colors">
              <ArrowLeft size={14} /> All Posts
            </Link>
            <button onClick={handleShare}
              className="flex items-center gap-1.5 text-xs font-mono text-[#9CA3AF] hover:text-[#0047AB] transition-colors">
              {copied ? <><CheckCircle size={12} className="text-emerald-500" /> Copied!</> : <><Share2 size={12} /> Share</>}
            </button>
          </div>
        </div>

        {/* Article */}
        <article className="max-w-3xl mx-auto px-4 sm:px-6 py-10 md:py-14">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
            {/* Category + meta */}
            <div className="flex flex-wrap items-center gap-3 mb-6">
              <span className={`px-2.5 py-1 text-xs font-mono border rounded-full ${getCategoryStyle(post.category)}`}>
                {post.category}
              </span>
              <span className="flex items-center gap-1 text-xs text-[#9CA3AF] font-body">
                <Calendar size={11} />{formatDate(post.created_at)}
              </span>
              <span className="flex items-center gap-1 text-xs text-[#9CA3AF] font-body">
                <Clock size={11} />{post.read_time} min read
              </span>
            </div>

            {/* Title */}
            <h1 className="font-sans font-bold text-3xl md:text-4xl tracking-tight text-[#111111] leading-tight mb-6">
              {post.title}
            </h1>

            {/* Excerpt / lead */}
            <p className="font-body text-[#374151] text-lg leading-relaxed border-l-2 border-[#0047AB] pl-4 mb-8 italic">
              {post.excerpt}
            </p>

            {/* Author */}
            <div className="flex items-center gap-3 mb-10 pb-8 border-b border-[#E5E7EB]">
              <div className="w-10 h-10 rounded-full bg-[#EBF2FF] border border-[#0047AB]/15 flex items-center justify-center">
                <span className="font-mono text-[#0047AB] text-xs font-bold">AR</span>
              </div>
              <div>
                <div className="font-sans font-semibold text-[#111111] text-sm">Akshay Rao</div>
                <div className="font-body text-[#9CA3AF] text-xs">IT Support & Implementation Specialist, Melbourne</div>
              </div>
            </div>

            {/* Article content */}
            <div
              className="prose-content font-body text-[#374151] leading-relaxed"
              dangerouslySetInnerHTML={{ __html: post.content }}
            />

            {/* Tags */}
            {post.tags?.length > 0 && (
              <div className="flex flex-wrap gap-2 mt-10 pt-6 border-t border-[#E5E7EB]">
                {post.tags.map(tag => (
                  <span key={tag} className="flex items-center gap-1 px-2.5 py-1 text-xs font-mono text-[#6B7280] bg-[#F3F4F6] border border-[#E5E7EB] rounded-lg">
                    <Tag size={9} />{tag}
                  </span>
                ))}
              </div>
            )}
          </motion.div>

          {/* CTA Card */}
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }} transition={{ duration: 0.5 }}
            className="mt-12 p-7 bg-[#0047AB] rounded-2xl text-white">
            <div className="font-mono text-white/60 text-[10px] uppercase tracking-[0.2em] mb-2">Ready to get started?</div>
            <h3 className="font-sans font-bold text-xl mb-2">Need IT help for your Melbourne business?</h3>
            <p className="font-body text-white/75 text-sm leading-relaxed mb-5">
              Book a 30-minute consultation for $8.99 and get a clear plan with no obligation.
            </p>
            <div className="flex flex-wrap gap-3">
              <Link to="/#contact"
                className="flex items-center gap-2 px-5 py-2.5 bg-white text-[#0047AB] text-sm font-semibold rounded-xl hover:bg-[#EBF2FF] transition-colors">
                Book a consultation <ArrowRight size={13} />
              </Link>
              <Link to="/blog"
                className="flex items-center gap-2 px-5 py-2.5 border border-white/30 text-white text-sm rounded-xl hover:bg-white/10 transition-colors">
                More articles
              </Link>
            </div>
          </motion.div>

          {/* Related posts */}
          {related.length > 0 && (
            <div className="mt-12">
              <div className="font-mono text-[#0047AB] text-xs uppercase tracking-[0.25em] mb-5">Related Articles</div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {related.map(p => (
                  <Link key={p.slug} to={`/blog/${p.slug}`}
                    className="group p-5 bg-white border border-[#E5E7EB] rounded-xl hover:border-[#0047AB]/25 hover:shadow-card transition-all duration-200">
                    <div className="font-sans font-semibold text-[#111111] text-sm mb-1 group-hover:text-[#0047AB] transition-colors">
                      {p.title}
                    </div>
                    <div className="font-body text-[#9CA3AF] text-xs">{p.read_time} min read</div>
                  </Link>
                ))}
              </div>
            </div>
          )}
        </article>
      </div>
    </>
  )
}
